import type { ChangeEventHandler } from "react";
import { useFormContext } from "react-hook-form";

import { SubscriptionDefaultIcon } from "@jti/brand-icons/subscription-default";
import { ConsumablesFormStepSection } from "@jti/ui/components/ConsumablesFormStepSection";
import { RadioCard } from "@jti/ui/components/RadioCard";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { MonthlyPurchaseButtonContent } from "@product-details/components/MonthlyPurchaseButtonContent";
import type { SticksProductWithAggregations } from "@product-details/containers/SticksProductDetailsContainer/types";
import type { SticksFormValues } from "@product-details/forms/SticksForm/SticksForm.types";

import { OrderFrequencyGroupTitle } from "./OrderFrequencyGroupTitle";

const ORDER_FREQUENCY_GROUP_NAME: keyof SticksFormValues = "orderFrequency";

type OrderFrequencyGroupProps = {
  currentProduct: SticksProductWithAggregations;
  step: number;
  onChange?: ChangeEventHandler<HTMLInputElement>;
};

export const OrderFrequencyGroup = ({ currentProduct, step, onChange }: OrderFrequencyGroupProps) => {
  const { __ } = useTranslations();

  const { watch, register } = useFormContext<SticksFormValues>();

  const { orderFrequency, packagingPreference } = watch();

  const registerProps = register(ORDER_FREQUENCY_GROUP_NAME, { onChange });

  const isOnetimeDisabled =
    currentProduct.current.orderFrequency !== "onetime" &&
    !currentProduct?.aggregations?.onetime[packagingPreference]?.sku;

  return (
    <ConsumablesFormStepSection
      title={<OrderFrequencyGroupTitle step={step} orderFrequency={orderFrequency} />}
      data-testid="frequency-options"
    >
      <RadioCard
        groupName={ORDER_FREQUENCY_GROUP_NAME}
        value="onetime"
        title={__("sticksForm.purchase_options.one_time_purchase")}
        isDisabled={isOnetimeDisabled}
        {...registerProps}
      />
      <RadioCard
        groupName={ORDER_FREQUENCY_GROUP_NAME}
        value="subscription"
        title={__("sticksForm.purchase_options.subscription.monthly")}
        icon={<SubscriptionDefaultIcon width="27px" height="27px" />}
        description={<MonthlyPurchaseButtonContent />}
        isUnavailable={packagingPreference === "single_packs"}
        {...registerProps}
      />
    </ConsumablesFormStepSection>
  );
};
